import {ChangeDetectionStrategy, Component, computed, DestroyRef, inject, input, OnDestroy, OnInit, signal, viewChild} from "@angular/core"
import {takeUntilDestroyed} from "@angular/core/rxjs-interop"
import {MatDialog} from "@angular/material/dialog"
import {DialogComponent} from "@common/components/dialogs/dialog/dialog.component"
import {fetchThrowingErrors} from "@common/helpers/api/fetch"
import {mutateThrowingErrors} from "@common/helpers/api/mutate"
import {NotificationsService} from "@common/services/notifications/notifications.service"
import {RefreshService} from "@common/services/refresh/refresh.service"
import {ImageViewerComponent} from "@common/components/viewers"
import {ContentTypeModel, JobState, MaterialOutputType} from "@generated"
import {FlatThumbnailOptionLabels, PbrExportOptionLabels} from "@labels"
import {
    MaterialAssetGridDataFragment,
    MaterialAssetGridDataGQL,
    MaterialAssetGridJobStateGQL,
    MaterialAssetGridOutputFragment,
    MaterialAssetGridUnpinGQL,
} from "@platform/components/materials/material-asset-grid/material-asset-grid/material-asset-grid.generated"
import {PbrAssetCellComponent} from "@platform/components/materials/material-asset-grid/pbr-asset-cell/pbr-asset-cell.component"
import {PinnedAssetCellComponent} from "@platform/components/materials/material-asset-grid/pinned-asset-cell/pinned-asset-cell.component"
import {ShaderballAssetCellComponent} from "@platform/components/materials/material-asset-grid/shaderball-asset-cell/shaderball-asset-cell.component"
import {ThumbnailAssetCellComponent} from "@platform/components/materials/material-asset-grid/thumbnail-asset-cell/thumbnail-asset-cell.component"
import {TileableAssetCellComponent} from "@platform/components/materials/material-asset-grid/tileable-asset-cell/tileable-asset-cell.component"
import {materialExportStartedGeneration, MaterialOutputService} from "@platform/services/material/material-output.service"
import {BakeTextureService} from "@template-editor/services/bake-texture.service"
import {MeshGeometryService} from "@template-editor/services/mesh-geometry.service"
import {SceneManagerService} from "@template-editor/services/scene-manager.service"
import {Subject, switchMap, takeUntil, timer} from "rxjs"

@Component({
    changeDetection: ChangeDetectionStrategy.OnPush,
    selector: "cm-material-asset-grid",
    templateUrl: "./material-asset-grid.component.html",
    styleUrls: ["./material-asset-grid.component.scss"],
    imports: [
        ImageViewerComponent,
        PbrAssetCellComponent,
        PinnedAssetCellComponent,
        ShaderballAssetCellComponent,
        ThumbnailAssetCellComponent,
        TileableAssetCellComponent,
    ],
    providers: [SceneManagerService, MeshGeometryService, BakeTextureService, MaterialOutputService],
})
export class MaterialAssetGridComponent implements OnInit, OnDestroy {
    private readonly dataGql = inject(MaterialAssetGridDataGQL)
    private readonly jobStateGql = inject(MaterialAssetGridJobStateGQL)
    private readonly unpinGql = inject(MaterialAssetGridUnpinGQL)
    private readonly destroyRef = inject(DestroyRef)
    private readonly matDialog = inject(MatDialog)
    private readonly materialOutputService = inject(MaterialOutputService)
    private readonly notifications = inject(NotificationsService)
    private readonly refresh = inject(RefreshService)
    private readonly sceneManagerService = inject(SceneManagerService)

    readonly $materialId = input.required<string>({alias: "materialId"})
    readonly $canEdit = input<boolean>(false, {alias: "canEdit"})
    readonly $canDownload = input<boolean>(false, {alias: "canDownload"})

    readonly $imageViewer = viewChild.required<ImageViewerComponent>("imageViewer")
    readonly $thumbnailCell = viewChild<ThumbnailAssetCellComponent>("thumbnailCell")

    readonly $material = signal<MaterialAssetGridDataFragment | undefined>(undefined)
    private readonly $pendingOutputTypes = signal<ReadonlySet<MaterialOutputType>>(new Set())
    private readonly stopPollingJobs$ = new Subject<void>()

    // A material can carry several outputs of the same type (one per revision). The asset cells
    // resolve the canonical output as the LAST match; use the same rule here so the action a
    // handler fires on (rerender/remove/cancel) targets the output the cell actually displays.
    readonly $shaderBallOutput = computed(() => this.$material()?.outputs.findLast((o) => o.type === MaterialOutputType.ShaderBall))
    readonly $shaderBallPending = computed(() => this.$pendingOutputTypes().has(MaterialOutputType.ShaderBall))

    readonly $tileableOutput = computed(() => this.$material()?.outputs.findLast((o) => o.type === MaterialOutputType.TileableImage))
    readonly $tileablePending = computed(() => this.$pendingOutputTypes().has(MaterialOutputType.TileableImage))

    readonly $thumbnailOutputs = computed<readonly MaterialAssetGridOutputFragment[]>(
        () => this.$material()?.outputs.filter((o) => FlatThumbnailOptionLabels.has(o.type)) ?? [],
    )
    readonly $thumbnailPendingTypes = computed<ReadonlySet<MaterialOutputType>>(
        () => new Set([...this.$pendingOutputTypes()].filter((t) => FlatThumbnailOptionLabels.has(t))),
    )

    readonly $pbrExportOutputs = computed<readonly MaterialAssetGridOutputFragment[]>(
        () => this.$material()?.outputs.filter((o) => PbrExportOptionLabels.has(o.type)) ?? [],
    )
    readonly $pbrExportPendingTypes = computed<ReadonlySet<MaterialOutputType>>(
        () => new Set([...this.$pendingOutputTypes()].filter((t) => PbrExportOptionLabels.has(t))),
    )

    // Mirror the thumbnail cell's own live size selection so the lightbox shows exactly the thumbnail
    // currently displayed in the tile — or nothing, when the selected size has not been rendered.
    readonly $selectedThumbnailOutput = computed(() => this.$thumbnailCell()?.$selectedOutput())

    readonly $hasCyclesMaterial = computed(() => !!this.$material()?.latestCyclesRevision)

    readonly $pinnedImage = computed(() => this.$material()?.pinnedImage ?? null)

    // Data-object ids of the rendered images currently shown in the grid, in display order, so the
    // lightbox can cycle through them. Only complete image outputs contribute an id.
    readonly $galleryImageIds = computed<string[]>(() => {
        const ids: string[] = []
        const shaderBall = this.$shaderBallOutput()?.result?.id
        if (shaderBall) ids.push(shaderBall)
        const thumbnail = this.$selectedThumbnailOutput()?.result?.id
        if (thumbnail) ids.push(thumbnail)
        const tileable = this.$tileableOutput()?.result?.id
        if (tileable) ids.push(tileable)
        const pinned = this.$pinnedImage()?.id
        if (pinned) ids.push(pinned)
        return ids
    })

    readonly $tileableDimensionsCm = computed<{width: number; height: number} | null>(() => {
        const revision = this.$material()?.textureGroup?.textureSets[0]?.textureSetRevisions[0]
        if (!revision) return null
        return {width: revision.width, height: revision.height}
    })

    onShaderBallPreview() {
        this.openImageViewer(this.$shaderBallOutput()?.result?.id)
    }
    onThumbnailPreview() {
        this.openImageViewer(this.$selectedThumbnailOutput()?.result?.id)
    }
    onTileablePreview() {
        this.openImageViewer(this.$tileableOutput()?.result?.id)
    }
    onPinnedPreview() {
        this.openImageViewer(this.$pinnedImage()?.id)
    }

    private openImageViewer(startDataObjectId: string | undefined) {
        if (!startDataObjectId) return
        void this.$imageViewer().openViewer(startDataObjectId, this.$galleryImageIds())
    }

    ngOnInit() {
        this.sceneManagerService.$lodType.set("pathTraced")
        this.sceneManagerService.$computeDummyReifiedMeshData.set(true)

        this.refresh
            .observeItem$({id: this.$materialId(), __typename: ContentTypeModel.Material}, true)
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(() => void this.loadData())
    }

    ngOnDestroy() {
        this.stopPollingJobs$.next()
        this.stopPollingJobs$.complete()
    }

    private async loadData() {
        const {material} = await fetchThrowingErrors(this.dataGql)({id: this.$materialId()})
        this.$material.set(material)
        this.$pendingOutputTypes.update((types) => {
            const next = new Set(types)
            for (const output of material.outputs) next.delete(output.type)
            return next
        })
        this.startJobStatusPolling(material.outputs)
    }

    private startJobStatusPolling(outputs: readonly MaterialAssetGridOutputFragment[]) {
        this.stopPollingJobs$.next()
        for (const output of outputs) {
            if (!output.job || !this.isJobRunning(output.job.state)) continue
            const jobId = output.job.id
            timer(5_000, 10_000)
                .pipe(
                    takeUntil(this.stopPollingJobs$),
                    switchMap(async () => {
                        const {job} = await fetchThrowingErrors(this.jobStateGql)({id: jobId})
                        if (!this.isJobRunning(job.state)) {
                            this.refresh.item({id: this.$materialId(), __typename: ContentTypeModel.Material})
                        }
                    }),
                )
                .subscribe()
        }
    }

    private isJobRunning(state: JobState) {
        return state === JobState.Init || state === JobState.Runnable || state === JobState.Running
    }

    private async createOutput(type: MaterialOutputType) {
        const materialRevisionId = this.$material()?.latestCyclesRevision?.id
        if (!materialRevisionId) return
        this.$pendingOutputTypes.update((types) => new Set(types).add(type))
        const clearPending = () =>
            this.$pendingOutputTypes.update((types) => {
                const next = new Set(types)
                next.delete(type)
                return next
            })
        try {
            const result = await this.materialOutputService.createOutput({
                materialId: this.$materialId(),
                materialRevisionId,
                type,
                showNotification: true,
                rethrowCaughtError: true,
            })
            // An export ensure reports a refusal instead of throwing one, and a refusal creates no
            // output row — so `loadData`, which clears the spinner only for types that now have a
            // row, would leave this one spinning for good.
            if (result !== undefined && !materialExportStartedGeneration(result)) {
                clearPending()
            }
            await this.loadData()
        } catch (error) {
            clearPending()
        }
    }

    private async cancelAndRemoveOutput(output: MaterialAssetGridOutputFragment) {
        await this.materialOutputService.cancelAndRemoveOutput({materialOutput: output, showNotification: true})
        await this.loadData()
    }

    private async restartOutput(output: MaterialAssetGridOutputFragment) {
        await this.materialOutputService.restartOutput({materialOutput: output, showNotification: true})
        await this.loadData()
    }

    private async confirmRemove(output: MaterialAssetGridOutputFragment) {
        const confirmed = await new Promise<boolean>((resolve) => {
            this.matDialog
                .open(DialogComponent, {
                    disableClose: false,
                    width: "400px",
                    data: {
                        title: "Delete item",
                        message: "Are you sure you want to remove the file?",
                        confirmLabel: "Delete item",
                        cancelLabel: "Cancel",
                    },
                })
                .afterClosed()
                .subscribe((value) => resolve(!!value))
        })
        if (!confirmed) return
        await this.cancelAndRemoveOutput(output)
    }

    onShaderBallRender() {
        void this.createOutput(MaterialOutputType.ShaderBall)
    }
    onShaderBallRerender() {
        const output = this.$shaderBallOutput()
        if (!output) return
        void (async () => {
            await this.cancelAndRemoveOutput(output)
            await this.createOutput(MaterialOutputType.ShaderBall)
        })()
    }
    onShaderBallRemove() {
        const output = this.$shaderBallOutput()
        if (output) void this.confirmRemove(output)
    }
    onShaderBallCancel() {
        const output = this.$shaderBallOutput()
        if (output) void this.cancelAndRemoveOutput(output)
    }
    onShaderBallRetry() {
        const output = this.$shaderBallOutput()
        if (output) void this.restartOutput(output)
    }

    onTileableRender() {
        void this.createOutput(MaterialOutputType.TileableImage)
    }
    onTileableRerender() {
        const output = this.$tileableOutput()
        if (!output) return
        void (async () => {
            await this.cancelAndRemoveOutput(output)
            await this.createOutput(MaterialOutputType.TileableImage)
        })()
    }
    onTileableRemove() {
        const output = this.$tileableOutput()
        if (output) void this.confirmRemove(output)
    }
    onTileableCancel() {
        const output = this.$tileableOutput()
        if (output) void this.cancelAndRemoveOutput(output)
    }
    onTileableRetry() {
        const output = this.$tileableOutput()
        if (output) void this.restartOutput(output)
    }

    private thumbnailOutputForType(type: MaterialOutputType) {
        return this.$thumbnailOutputs().findLast((o) => o.type === type)
    }

    onThumbnailRender(type: MaterialOutputType) {
        void this.createOutput(type)
    }
    onThumbnailRerender(type: MaterialOutputType) {
        const output = this.thumbnailOutputForType(type)
        if (!output) return
        void (async () => {
            await this.cancelAndRemoveOutput(output)
            await this.createOutput(type)
        })()
    }
    onThumbnailRemove(type: MaterialOutputType) {
        const output = this.thumbnailOutputForType(type)
        if (output) void this.confirmRemove(output)
    }
    onThumbnailCancel(type: MaterialOutputType) {
        const output = this.thumbnailOutputForType(type)
        if (output) void this.cancelAndRemoveOutput(output)
    }
    onThumbnailRetry(type: MaterialOutputType) {
        const output = this.thumbnailOutputForType(type)
        if (output) void this.restartOutput(output)
    }

    private pbrOutputForType(type: MaterialOutputType) {
        return this.$pbrExportOutputs().findLast((o) => o.type === type)
    }

    onPbrRender(type: MaterialOutputType) {
        void this.createOutput(type)
    }
    onPbrRerender(type: MaterialOutputType) {
        const output = this.pbrOutputForType(type)
        if (!output) return
        void (async () => {
            await this.cancelAndRemoveOutput(output)
            await this.createOutput(type)
        })()
    }
    onPbrRemove(type: MaterialOutputType) {
        const output = this.pbrOutputForType(type)
        if (output) void this.confirmRemove(output)
    }
    onPbrCancel(type: MaterialOutputType) {
        const output = this.pbrOutputForType(type)
        if (output) void this.cancelAndRemoveOutput(output)
    }
    onPbrRetry(type: MaterialOutputType) {
        const output = this.pbrOutputForType(type)
        if (output) void this.restartOutput(output)
    }

    async onUnpin() {
        await mutateThrowingErrors(this.unpinGql)({input: {materialId: this.$materialId()}})
        this.refresh.item({id: this.$materialId(), __typename: ContentTypeModel.Material})
    }
}
